import React from 'react';

/** The latest request of each field. ``start`` returns whether it is still the latest: starting another for the field, as a
 * manual edit does, or ``cancelAll``, as a reset does, makes it stale, so a late answer changes nothing. */
export function pinyinRequests<Key>() {
    let next = 0;
    const latest = new Map<Key, number>();
    return {
        start(key: Key) {
            const request = ++next;
            latest.set(key, request);
            return () => latest.get(key) === request;
        },
        cancelAll() {
            latest.clear();
        },
    };
}

export function usePinyinRequests<Key>() {
    return React.useState(() => pinyinRequests<Key>())[0];
}

export interface LineReading { line: string; reading: string[] }

/** A multi-line field's lines with their readings as they are now, none where its pinyin line is blank, so a line left as it
 * was keeps its reading when another changes. Suggested pinyin leaves out the text's blank lines; with some of it blank
 * too, the lines pair up by place. */
export function lineReadings(text: string, pinyin: string): LineReading[] {
    const textLines = text.split('\n').map(line => line.trim());
    const pinyinLines = pinyin.split('\n').map(line => line.trim());
    const lines = textLines.filter(Boolean);
    const written = pinyinLines.filter(Boolean);
    const readings = written.length === lines.length ? written : textLines.flatMap((line, index) => (line ? [pinyinLines[index] ?? ''] : []));
    return lines.map((line, index) => ({ line, reading: readings[index].split(/\s+/).filter(Boolean) }));
}

/** What each line keeps after an edit. The lines before and after what changed are the same lines, so they keep their own
 * readings, whatever other lines hold the same characters (行 is xíng in one line, háng in the next). Inside what changed,
 * a line takes the reading of a line of the same text, as a moved line would. */
export function keptReadings(before: LineReading[], lines: string[]): (string[] | undefined)[] {
    let start = 0;
    while (start < before.length && start < lines.length && before[start].line === lines[start]) start++;
    let end = 0;
    while (end < before.length - start && end < lines.length - start
        && before[before.length - 1 - end].line === lines[lines.length - 1 - end]) end++;
    const edited = before.slice(start, before.length - end);
    return lines.map((line, index) => {
        if (index < start) return before[index].reading;
        if (index >= lines.length - end) return before[before.length - (lines.length - index)].reading;
        const found = edited.findIndex(row => row.line === line);
        return found < 0 ? undefined : edited.splice(found, 1)[0].reading;
    });
}
